import { useGoogleMap } from '@react-google-maps/api';
import { useEffect, useRef } from 'react';

// Status-Farben
const STATUS_COLORS = {
  available:     { bg: '#22C55E', border: '#16A34A', pulse: '#22C55E' },
  en_route:      { bg: '#F59E0B', border: '#D97706', pulse: '#F59E0B' },
  with_customer: { bg: '#EF4444', border: '#DC2626', pulse: '#EF4444' },
  paused:        { bg: '#3B82F6', border: '#2563EB', pulse: '#3B82F6' },
  offline:       { bg: '#9CA3AF', border: '#6B7280', pulse: '#9CA3AF' },
};

// CSS für Pulsier-Animation (wird einmalig injected)
let styleInjected = false;
const injectStyles = () => {
  if (styleInjected) return;
  styleInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    @keyframes marker-pulse {
      0% { transform: scale(1); opacity: 0.6; }
      50% { transform: scale(1.8); opacity: 0; }
      100% { transform: scale(1); opacity: 0; }
    }
    .ncb-marker { transition: transform 0.2s ease; }
    .ncb-marker:hover { transform: scale(1.15); }
  `;
  document.head.appendChild(style);
};

/**
 * Erstellt ein Custom-HTML-Element als Marker-Content
 */
const createCustomMarkerContent = ({ status, label, isRealtime }) => {
  const colors = STATUS_COLORS[status] || STATUS_COLORS.offline;

  const container = document.createElement('div');
  container.className = 'ncb-marker';
  container.style.cssText = 'position:relative;cursor:pointer;';

  // Pulsier-Ring für Live-Fahrer
  if (isRealtime && status !== 'offline') {
    const pulse = document.createElement('div');
    pulse.style.cssText = `
      position:absolute;top:50%;left:50%;
      width:40px;height:40px;
      margin-top:-20px;margin-left:-20px;
      border-radius:50%;
      background:${colors.pulse};
      animation:marker-pulse 2s ease-out infinite;
      pointer-events:none;
    `;
    container.appendChild(pulse);
  }

  // Hauptkreis
  const circle = document.createElement('div');
  circle.style.cssText = `
    position:relative;
    width:36px;height:36px;
    border-radius:50%;
    background:${colors.bg};
    border:3px solid ${colors.border};
    display:flex;align-items:center;justify-content:center;
    box-shadow:0 2px 8px rgba(0,0,0,0.3);
    color:white;font-weight:700;font-size:13px;
    font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
    text-transform:uppercase;
    letter-spacing:0.5px;
  `;

  // Taxi-Icon SVG oder Initialen
  if (label) {
    circle.textContent = label;
  } else {
    // Kleines Taxi-SVG als Fallback
    circle.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="white">
      <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
    </svg>`;
  }
  container.appendChild(circle);

  // Kleiner Pfeil nach unten (Zeiger)
  const arrow = document.createElement('div');
  arrow.style.cssText = `
    width:0;height:0;
    border-left:6px solid transparent;
    border-right:6px solid transparent;
    border-top:8px solid ${colors.border};
    margin:0 auto;
    margin-top:-1px;
  `;
  container.appendChild(arrow);

  return container;
};

/**
 * AdvancedMarker-Komponente mit Custom HTML Content
 *
 * Neue Props:
 * - status: Fahrer-Status ('available', 'en_route', 'with_customer', 'paused', 'offline')
 * - label: Initialen oder kurzer Text für den Marker
 * - isRealtime: Pulsier-Animation für Live-Position
 *
 * Legacy Props (Fallback):
 * - icon: { url, scaledSize } — wird in Farbe konvertiert
 */
function AdvancedMarker({
  position,
  title,
  icon,
  status,
  label,
  isRealtime,
  onClick,
  zIndex
}) {
  const mapInstance = useGoogleMap();
  const markerRef = useRef(null);
  const retryCountRef = useRef(0);

  useEffect(() => {
    injectStyles();
  }, []);

  useEffect(() => {
    if (!mapInstance || !window.google) return;

    const createMarker = () => {
      const isAdvancedMarkerAvailable =
        window.google.maps.marker &&
        typeof window.google.maps.marker.AdvancedMarkerElement === 'function';

      if (isAdvancedMarkerAvailable) {
        // Bestehenden Marker entfernen
        if (markerRef.current) {
          markerRef.current.map = null;
        }

        const options = {
          position,
          title,
          map: mapInstance,
          zIndex,
        };

        // Custom HTML Content wenn status vorhanden, sonst PinElement Fallback
        if (status) {
          options.content = createCustomMarkerContent({ status, label, isRealtime });
        } else if (icon) {
          // Legacy: PinElement aus icon.url Farbe ableiten
          const pin = new window.google.maps.marker.PinElement({
            scale: icon.scaledSize ? (icon.scaledSize.width / 32) : 1,
            background: icon.url ?
              icon.url.includes('green') ? '#22C55E' :
              icon.url.includes('blue') ? '#3B82F6' :
              icon.url.includes('red') ? '#EF4444' :
              icon.url.includes('gray') ? '#9CA3AF' : '#F59E0B'
              : '#F59E0B'
          });
          options.content = pin.element;
        }

        const marker = new window.google.maps.marker.AdvancedMarkerElement(options);

        if (onClick) {
          marker.addListener('gmp-click', onClick);
        }

        markerRef.current = marker;
      } else {
        // Retry-Logik
        if (retryCountRef.current < 2) {
          retryCountRef.current += 1;
          setTimeout(() => createMarker(), 500);
          return;
        }

        // Fallback: Legacy Marker
        if (markerRef.current) {
          markerRef.current.setMap(null);
        }

        const marker = new window.google.maps.Marker({
          position,
          title,
          icon,
          map: mapInstance,
          zIndex
        });

        if (onClick) {
          marker.addListener('click', onClick);
        }

        markerRef.current = marker;
      }
    };

    retryCountRef.current = 0;
    createMarker();

    return () => {
      if (markerRef.current) {
        if (markerRef.current.setMap) {
          markerRef.current.setMap(null);
        } else {
          markerRef.current.map = null;
        }
        markerRef.current = null;
      }
    };
  }, [mapInstance, position, title, icon, status, label, isRealtime, onClick, zIndex]);

  return null;
}

export default AdvancedMarker;
